import { useState } from 'react';

import { CheckBox, DatePicker, NumericTextBox, TextBox } from '@/components/ui';
import { parseApiDate } from '@/utils/date';

import { readField, toCellText } from './cellValue';
import type { GridColumn } from './types';

export interface GridCellEditorProps<T> {
  row: T;
  column: GridColumn<T>;
}

/**
 * Standardeditor einer Zelle ohne eigenes `editTemplate`. Er schreibt — wie die
 * Bearbeitungs-Templates aus REQ-09 und wie `@bind-Value` im Altclient — direkt in das
 * übergebene Modellobjekt; das Bestätigen und Schließen übernimmt `GridCell`.
 */
export function GridCellEditor<T>(props: GridCellEditorProps<T>) {
  const { column, row } = props;
  const [value, setValue] = useState<unknown>(() => readField(row, column.field));

  const write = (next: unknown) => {
    (row as unknown as Record<string, unknown>)[column.field] = next;
    setValue(next);
  };

  if (column.valueType === 'boolean') {
    return <CheckBox checked={value === true} onChange={write} />;
  }

  if (column.valueType === 'number' || column.valueType === 'integer') {
    return (
      <NumericTextBox
        value={typeof value === 'number' ? value : null}
        format={column.valueType === 'integer' ? 'd' : 'N2'}
        onChange={write}
      />
    );
  }

  if (column.valueType === 'date') {
    return (
      <DatePicker
        value={parseApiDate(value as string | Date | null | undefined)}
        onChange={(date) => {
          // Die API erwartet Datumswerte als ISO-Zeichenkette (REQ-00 §7).
          write(date === null ? null : date.toISOString());
        }}
      />
    );
  }

  return <TextBox value={toCellText(value)} onChange={write} />;
}

export default GridCellEditor;
